/**
 * Fairness algorithm v1 — the browser half of the provably-fair promise.
 *
 * This is a port of api/internal/fair (drbg.go, merkle.go, v1.go). The two
 * implementations are checked against the same fixture,
 * `tests/fair-v1-vectors.json`, by Go tests and by v1.test.ts: if either side
 * drifts, both suites fail. That shared fixture is the only thing that makes
 * "пересчитай сам" meaningful — a verifier that disagrees with the server is
 * worse than no verifier, because it manufactures distrust instead of
 * resolving it.
 *
 * Everything is async because Web Crypto is. The cost is one `await` per block
 * of DRBG output; the benefit is that hashing is the platform's audited
 * implementation rather than a hand-written SHA-256.
 */
import {
  bytesToHex,
  concat,
  encodeContext,
  hexToBytes,
  readUint64BE,
  uint32BE,
  uint64BE,
  utf8,
  equalBytes,
  subtle,
} from './bytes';

export const ALGO_V1 = 'v1';
export const LABEL_DRAW = 'prizma.draw.v1';
export const LABEL_INSTANT = 'prizma.instant.v1';
export const SERVER_SEED_BYTES = 32;
export const TICKET_LEAF_LABEL = 'prizma.ticket.v1';

const LEAF_PREFIX = 0x00;
const NODE_PREFIX = 0x01;

export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await subtle().digest('SHA-256', data as BufferSource));
}

async function importHmacKey(key: Uint8Array): Promise<CryptoKey> {
  return subtle().importKey('raw', key as BufferSource, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
}

export async function hmacSha256(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const k = await importHmacKey(key);
  return new Uint8Array(await subtle().sign('HMAC', k, data as BufferSource));
}

/** SHA-256(serverSeed) — the value published before a campaign opens. */
export async function commit(serverSeed: Uint8Array): Promise<string> {
  return bytesToHex(await sha256(serverSeed));
}

/** Checks a revealed seed against its published commitment. */
export async function verifyCommit(serverSeed: Uint8Array, commitHex: string): Promise<boolean> {
  return equalBytes(await sha256(serverSeed), hexToBytes(commitHex));
}

/**
 * HMAC-SHA256 counter-mode byte stream, keyed with the committed server seed.
 *
 * Block i = HMAC(seed, label ‖ 0x00 ‖ context ‖ uint64be(i)).
 */
export class DRBG {
  private readonly label: Uint8Array;
  private readonly context: Uint8Array;
  private key: CryptoKey | null = null;
  private counter = 0n;
  // Тип указан явно: без него поле выводится как Uint8Array<ArrayBuffer>,
  // а concat() возвращает Uint8Array<ArrayBufferLike> (TS 5.7+).
  private buf: Uint8Array = new Uint8Array(0);

  constructor(
    private readonly seed: Uint8Array,
    label: string,
    context: Uint8Array,
  ) {
    this.label = utf8(label);
    this.context = context;
  }

  private async block(): Promise<void> {
    this.key ??= await importHmacKey(this.seed);
    const message = concat(this.label, new Uint8Array([0x00]), this.context, uint64BE(this.counter));
    this.counter += 1n;
    const digest = new Uint8Array(await subtle().sign('HMAC', this.key, message as BufferSource));
    this.buf = concat(this.buf, digest);
  }

  async read(n: number): Promise<Uint8Array> {
    while (this.buf.length < n) await this.block();
    const out = this.buf.slice(0, n);
    this.buf = this.buf.slice(n);
    return out;
  }

  async uint64(): Promise<bigint> {
    return readUint64BE(await this.read(8));
  }

  /**
   * Uniform value in [0, n), by bitmask rejection sampling.
   *
   * `% n` would bias the low values whenever n is not a power of two. With a
   * four-sector prize table that bias is measurable, and a measurably unfair
   * "provably fair" wheel is the worst possible outcome for this product.
   */
  async intN(n: bigint): Promise<bigint> {
    if (n <= 1n) return 0n;
    const bits = (n - 1n).toString(2).length;
    const mask = (1n << BigInt(bits)) - 1n;
    for (;;) {
      const v = (await this.uint64()) & mask;
      if (v < n) return v;
    }
  }
}

// ─── Merkle (RFC 6962) ───

export function ticketLeafData(number: string): Uint8Array {
  return concat(utf8(TICKET_LEAF_LABEL), new Uint8Array([0x00]), utf8(number));
}

export async function leafHash(data: Uint8Array): Promise<Uint8Array> {
  return sha256(concat(new Uint8Array([LEAF_PREFIX]), data));
}

export async function nodeHash(left: Uint8Array, right: Uint8Array): Promise<Uint8Array> {
  return sha256(concat(new Uint8Array([NODE_PREFIX]), left, right));
}

/** Largest power of two strictly below n. */
export function splitPoint(n: number): number {
  if (n < 2) return 0;
  return 1 << (31 - Math.clz32(n - 1));
}

export async function merkleRoot(leaves: Uint8Array[]): Promise<Uint8Array> {
  if (leaves.length === 0) return sha256(new Uint8Array(0));
  if (leaves.length === 1) return leaves[0] as Uint8Array;
  const k = splitPoint(leaves.length);
  return nodeHash(await merkleRoot(leaves.slice(0, k)), await merkleRoot(leaves.slice(k)));
}

export async function ticketLeaves(numbers: string[]): Promise<Uint8Array[]> {
  return Promise.all(numbers.map((n) => leafHash(ticketLeafData(n))));
}

/**
 * Merkle root of a ticket list, in the order given. The order is part of the
 * commitment: a reordered list has a different root.
 */
export async function ticketRoot(numbers: string[]): Promise<string> {
  return bytesToHex(await merkleRoot(await ticketLeaves(numbers)));
}

/** RFC 6962 PATH(m, D[n]) — sibling hashes from the leaf upward. */
export async function auditPath(index: number, leaves: Uint8Array[]): Promise<Uint8Array[]> {
  if (index < 0 || index >= leaves.length || leaves.length < 2) return [];
  const k = splitPoint(leaves.length);
  if (index < k) {
    return [...(await auditPath(index, leaves.slice(0, k))), await merkleRoot(leaves.slice(k))];
  }
  return [...(await auditPath(index - k, leaves.slice(k))), await merkleRoot(leaves.slice(0, k))];
}

/** RFC 6962 §2.1.1 inclusion verification. */
export async function verifyInclusion(
  leaf: Uint8Array,
  index: number,
  size: number,
  path: Uint8Array[],
  root: Uint8Array,
): Promise<boolean> {
  if (size <= 0 || index < 0 || index >= size) return false;
  let fn = index;
  let sn = size - 1;
  let r = leaf;
  for (const p of path) {
    if (sn === 0) return false;
    if ((fn & 1) === 1 || fn === sn) {
      r = await nodeHash(p, r);
      while ((fn & 1) === 0 && fn !== 0) {
        fn >>= 1;
        sn >>= 1;
      }
    } else {
      r = await nodeHash(r, p);
    }
    fn >>= 1;
    sn >>= 1;
  }
  return sn === 0 && equalBytes(r, root);
}

// ─── the draw ───

export interface DrawInput {
  campaignId: string;
  /** The public external value — a lottery draw, a beacon round. */
  externalSeed: string;
  /** Merkle root of the frozen ticket list, as hex. */
  ticketRoot: string;
  ticketCount: number;
  winnerCount: number;
}

/** Canonical transcript the DRBG is keyed over, matching DrawInput.Context in Go. */
export function drawContext(input: DrawInput): Uint8Array {
  return encodeContext(
    utf8(input.campaignId),
    utf8(input.externalSeed),
    hexToBytes(input.ticketRoot),
    uint32BE(input.ticketCount),
    uint32BE(input.winnerCount),
  );
}

/**
 * Recomputes the winners: `winnerCount` distinct 1-based positions out of
 * `ticketCount`, in prize order.
 *
 * A partial Fisher–Yates shuffle over a sparse view of the array, so the work
 * is proportional to the number of prizes rather than the number of tickets —
 * a verifier in a phone browser must not have to shuffle 18 740 entries.
 */
export async function drawV1(serverSeed: Uint8Array, input: DrawInput): Promise<number[]> {
  if (serverSeed.length !== SERVER_SEED_BYTES) {
    throw new Error(`сид сервера должен быть ${SERVER_SEED_BYTES} байт, получено ${serverSeed.length}`);
  }
  if (input.winnerCount <= 0) return [];
  if (input.ticketCount < input.winnerCount) {
    throw new Error(`билетов (${input.ticketCount}) меньше, чем победителей (${input.winnerCount})`);
  }

  const drbg = new DRBG(serverSeed, LABEL_DRAW, drawContext(input));
  const swapped = new Map<number, number>();
  const at = (i: number) => swapped.get(i) ?? i;

  const winners: number[] = [];
  for (let i = 0; i < input.winnerCount; i++) {
    const j = i + Number(await drbg.intN(BigInt(input.ticketCount - i)));
    const vi = at(i);
    const vj = at(j);
    winners.push(vj + 1);
    swapped.set(i, vj);
    swapped.set(j, vi);
  }
  return winners;
}

// ─── instant mechanics ───

export interface InstantProof {
  algo: string;
  label: string;
  commit: string;
  context_hex: string;
  draw: number;
  weight_total: number;
  index: number;
}

/**
 * Recomputes one instant outcome from a revealed seed and its published proof.
 *
 * Returns the index it derives plus whether that matches the proof, so the UI
 * can show both "what we got" and "what was claimed" rather than a bare
 * yes/no — a mismatch is information the participant should see in full.
 */
export async function verifyInstantOutcome(
  serverSeed: Uint8Array,
  proof: InstantProof,
  weights: number[],
): Promise<{ ok: boolean; computedIndex: number; reason?: string }> {
  if (!(await verifyCommit(serverSeed, proof.commit))) {
    return { ok: false, computedIndex: -1, reason: 'сид не соответствует опубликованному хешу' };
  }
  const total = weights.reduce((sum, w) => sum + (w > 0 ? w : 0), 0);
  if (total === 0) {
    return { ok: false, computedIndex: -1, reason: 'в таблице призов нет положительных весов' };
  }
  if (total !== proof.weight_total) {
    return {
      ok: false,
      computedIndex: -1,
      reason: `сумма весов ${total} не совпадает с зафиксированной ${proof.weight_total}`,
    };
  }

  const context = hexToBytes(proof.context_hex);
  const draw = await new DRBG(serverSeed, proof.label, context).intN(BigInt(total));
  if (draw !== BigInt(proof.draw)) {
    return { ok: false, computedIndex: -1, reason: `пересчитанное значение ${draw} ≠ ${proof.draw}` };
  }

  let index = weights.length - 1;
  let acc = 0;
  for (let i = 0; i < weights.length; i++) {
    const w = weights[i] as number;
    if (w <= 0) continue;
    acc += w;
    if (Number(draw) < acc) {
      index = i;
      break;
    }
  }
  return { ok: index === proof.index, computedIndex: index };
}

/** Builds an instant transcript, for verifying an outcome from its parts. */
export function instantContext(campaignId: string, codeHashHex: string, attempt: number): Uint8Array {
  return encodeContext(utf8(campaignId), hexToBytes(codeHashHex), uint32BE(attempt));
}
